import { PRINTER_EVENTS } from '~/main/namespace';
import { PRINT_EXECUTION_TIME_EVENTS, PRINTER_FORMATS } from '~/main/printer/types';
import { LoggerScope } from '~/main/utils/logger';
import { connectionActions } from '~/renderer/store/connection';
import { ANALYTICS_EVENTS } from '~/renderer/store/middleware/types';
import { receiptActions } from '~/renderer/store/receipt';
import { isClientSideReady, isRenderProcess } from '~/renderer/utils';
import { track } from '~/renderer/utils/analytics';
import Logger from '~/renderer/utils/logger';
import { keys } from '~/shared';

import type { AnyAction, Dispatch, Middleware } from '@reduxjs/toolkit';
import type { PrintEventData } from '~/renderer/hooks/use-event-listener';
import type { AppState } from '~/renderer/store/types';
import type { Dictionary } from '~/renderer/utils/types';

const isHubPage = isClientSideReady() && window.location.pathname.endsWith('/hub.html');
const isCfdPage =
  isClientSideReady() &&
  (window.location.pathname.endsWith('/cfd.html') ||
    // Support for the static assets CFD
    /retail-cfd\.lightspeedapp(stg)?\.com/.test(window.location.hostname));

if (isRenderProcess) {
  window.ipcRenderer?.on(PRINTER_EVENTS.PRINT_COMPLETED, async (_, data: PrintEventData) => {
    // Do not track if a command is executed
    if (data.format === PRINTER_FORMATS.COMMAND) return;

    if (data.executionTimes) {
      const trackedExecutionTimes: Dictionary<string, number> = {};
      let currentExecutionTime: number = 0;
      const getNextExecutionTime = (timestamp: number) => {
        const time = Math.round(timestamp - currentExecutionTime) / 1000;
        currentExecutionTime = timestamp;
        return time;
      };

      keys(PRINT_EXECUTION_TIME_EVENTS).forEach(event => {
        const dataExecutionTime = data.executionTimes?.[event];
        if (!currentExecutionTime) {
          currentExecutionTime = dataExecutionTime || 0;
        } else if (dataExecutionTime) {
          trackedExecutionTimes[event] = getNextExecutionTime(dataExecutionTime);
        }
      });

      Logger({ scope: LoggerScope.TIME }).debug({
        msg: ANALYTICS_EVENTS.PRINT_COMPLETED,
        executionTimes: trackedExecutionTimes,
      });
    }

    const { executionTimes, ...trackedData } = data;

    if (
      executionTimes[PRINT_EXECUTION_TIME_EVENTS.RETAIL_PRINT_START] &&
      executionTimes[PRINT_EXECUTION_TIME_EVENTS.HUB_PRINT_COMPLETED]
    ) {
      trackedData.executionTime =
        Math.round(
          executionTimes[PRINT_EXECUTION_TIME_EVENTS.HUB_PRINT_COMPLETED]! -
            executionTimes[PRINT_EXECUTION_TIME_EVENTS.RETAIL_PRINT_START]!,
        ) / 1000;

      Logger({ scope: LoggerScope.TIME }).debug({
        msg: ANALYTICS_EVENTS.PRINT_COMPLETED,
        executionTime: trackedData.executionTime,
      });
    }

    await track(
      ANALYTICS_EVENTS.PRINT_COMPLETED,
      trackedData as Omit<Required<PrintEventData>, 'executionTimes'>,
    );
  });
}

export const analytics: Middleware<AnyAction, AppState> =
  () => (next: Dispatch) => (action: AnyAction) => {
    // @Note hub page tracks the connection, the cfd page tracks the sale actions
    // and the web version tracks both because it doesn't have the hub
    (!isRenderProcess || isHubPage) && hubAnalytics(action);
    (!isRenderProcess || isCfdPage) && cfdAnalytics(action);

    return next(action);
  };

const hubAnalytics = async (action: AnyAction) => {
  if (connectionActions.updateConnectionError.match(action)) {
    await track(ANALYTICS_EVENTS.CONNECTION_ERROR);
  }
};

const cfdAnalytics = async (action: AnyAction) => {
  if (receiptActions.updateSaleCompleted.match(action) && action.payload.saleId) {
    await track(ANALYTICS_EVENTS.SALE_COMPLETED, {
      saleId: action.payload.saleId,
    });
  }
};
